<template>
  <Transition name="modal" :duration="200">
    <div v-if="isOpen" class="sheet">
      <div class="overlay" @click="onOverlayClick" />

      <div class="modal-container">
        <div class="modal" :class="`modal-${size}`">
          <div class="header">
            <h2>
              <slot name="title" />
            </h2>

            <AppButton v-if="closable" class="close" icon aria-label="Stäng" @click="close">
              <Close />
            </AppButton>
          </div>

          <div class="modal-content">
            <slot />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, onUnmounted } from 'vue'
import { useScrollLock } from '../../composables/scrollLock'

import AppButton from '../AppButton/AppButton.vue'
import Close from '../../icons/Close.vue'

const props = withDefaults(
  defineProps<{
    modelValue?: boolean // Vue 3 v-model
    value?: boolean // Vue 2 v-model
    closable?: boolean
    // `tall`: the default width, growing with its content up to the window's height less a margin.
    size?: 'default' | 'large' | 'tall'
  }>(),
  {
    modelValue: undefined,
    value: undefined,
    closable: true,
    size: 'default',
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void // Vue 3
  (e: 'input', v: boolean): void // Vue 2
}>()

const isOpen = computed(() => props.modelValue ?? props.value ?? false)

const close = () => {
  emit('update:modelValue', false)
  emit('input', false)
}

const { activateScrollLock, deactivateScrollLock } = useScrollLock()

watch(isOpen, (open) => {
  if (open) {
    activateScrollLock()
  } else {
    deactivateScrollLock()
  }
})

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.closable && isOpen.value) close()
}

onMounted(() => {
  if (isOpen.value) {
    activateScrollLock()
  }
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  deactivateScrollLock()
  window.removeEventListener('keydown', onKeydown)
})

const onOverlayClick = () => {
  if (!props.closable) return

  close()
}
</script>

<style lang="scss">
.modal-enter-active,
.modal-leave-active {
  .modal-container {
    transition: opacity 0.2s ease-in-out;
  }

  .overlay {
    transition: opacity 0.2s ease-in-out;
  }
}

.modal-enter-from,
.modal-leave-to {
  .modal-container {
    opacity: 0;
  }

  .overlay {
    opacity: 0;
  }
}
</style>

<style lang="scss" scoped>
.sheet {
  z-index: 12;
  position: fixed;
  inset: 0;
}

/* .modal-container was named .container but that collides with Tailwind's container utility in microscope,
   with a max-width so the modal would not center correctly on wide screens */
.modal-container {
  position: absolute;
  inset: 0;
  pointer-events: none;
  box-sizing: border-box;

  padding: 24px 16px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.modal-content {
  overflow-y: auto;
  min-height: 0;
  padding: 0 16px 16px;
  overscroll-behavior: contain;
}

.modal {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  width: 100%;
  margin: 0 auto;
  pointer-events: auto;

  background-color: var(--color-canvas-default);

  border-radius: 16px;

  @media screen and (min-width: 600px) {
    &.modal-default {
      width: min(100%, 900px);
      max-height: min(calc(100vh - 48px), 820px);
    }

    &.modal-tall {
      width: min(100%, 900px);
      max-height: calc(100vh - 48px);
    }

    &.modal-large {
      height: min(calc(100vh - 48px), 1200px);
      width: min(96vw, 1200px);
      max-height: min(calc(100vh - 48px), 1200px);

      .modal-content {
        display: flex;
        flex: 1;
        flex-direction: column;
      }
    }
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px;

  h2 {
    margin-bottom: 0;
  }

  button.close {
    border-radius: 50%;
    cursor: pointer;
    color: var(--color-fg-default);

    border: none;
    background-color: transparent;

    &.icon {
      padding: 0;
    }

    &:hover {
      background-color: var(--color-overlay-hover);
    }
  }
}

.overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
}
</style>
